import { useNavigate } from 'react-router-dom'
import { FiInfo } from 'react-icons/fi'
import { faFull, faPlain } from 'utilities/helper/format'

/**
 * One row of فهرست اعضا, shared by the dashboard card and the full page.
 *
 * The numeric column and the bar both show WHATEVER the list is sorted by.
 * A row that always showed capital while the list was ranked by debt would
 * make the ordering look broken.
 */

export const SORTS: {
    value: TMembersSort
    label: string
    unit: string
    color: string
    /**
     * How the bar is scaled.
     *
     * 'absolute' — the value IS a 0..100 position and the bar is that
     * position. Percentile must never be re-normalised against the page's
     * own maximum: on the first page every member is near the top of the
     * fund, so dividing by the page max painted eight identical full bars
     * (۹۹٪, ۹۹٪, ۹۸٪ …) and the bar stopped carrying information exactly
     * where a manager reads it.
     *
     * 'max' — money and days have no natural ceiling, so the biggest row on
     * screen sets the scale.
     */
    scale: 'absolute' | 'max'
    read: (row: IAdminMemberRow) => number
    text: (row: IAdminMemberRow) => string
    /**
     * Whether this row's value is bad news, so the table can paint it red.
     * Lives on the column rather than in the cell, because the table now
     * renders its cells FROM this list — see MemberTable.
     */
    alarm?: (row: IAdminMemberRow) => boolean
}[] = [
    {
        value: 'score',
        scale: 'absolute',
        label: 'امتیاز',
        unit: 'صدک',
        color: '#0A93A6',
        read: (row) => row.percentile,
        text: (row) => `${faPlain.format(row.percentile)}٪`,
    },
    {
        value: 'capital',
        scale: 'max',
        label: 'سرمایه',
        unit: 'تومان',
        color: '#0A93A6',
        read: (row) => row.capital,
        text: (row) => faFull.format(row.capital),
    },
    // «بدهی» on its own reads as «بدهی سررسیدشده» to a Persian speaker, so
    // neither meaning gets that word to itself. Both are offered, each
    // naming which one it is.
    {
        value: 'owed',
        scale: 'max',
        label: 'مانده وام',
        unit: 'تومان',
        color: '#7C4DBE',
        read: (row) => row.loan_outstanding,
        text: (row) => faFull.format(row.loan_outstanding),
    },
    {
        value: 'overdue',
        scale: 'max',
        label: 'بدهی سررسیدشده',
        unit: 'تومان',
        color: '#F24C3D',
        read: (row) => row.overdue_amount,
        text: (row) => faFull.format(row.overdue_amount),
        alarm: (row) => row.overdue_amount > 0,
    },
    {
        value: 'late',
        scale: 'max',
        label: 'تأخیر',
        unit: 'روز',
        color: '#F24C3D',
        read: (row) => row.max_days_overdue,
        text: (row) => faPlain.format(row.max_days_overdue),
        alarm: (row) => row.max_days_overdue > 0,
    },
]

export const TAGS: { value: TMembersTag; label: string }[] = [
    { value: 'all', label: 'همه' },
    { value: 'loan', label: 'دارای وام' },
    { value: 'overdue', label: 'معوق' },
    { value: 'stopped', label: 'توقف پرداخت' },
]

function chipFor(row: IAdminMemberRow) {
    if (row.tags.includes('overdue'))
        return {
            text: `${faPlain.format(row.max_days_overdue)} روز معوق`,
            className: ' bg-danger-soft text-danger-on',
        }
    if (row.tags.includes('stopped'))
        return { text: 'توقف پرداخت', className: ' bg-warning-soft text-warning-on' }
    return null
}

export function ScoreInfoDot({ onClick }: { onClick: () => void }) {
    // The hollow (i) the dashboard's Hint and the grant wizard's CandidateRow
    // already use for exactly this gesture. A filled green disc reads as a
    // STATUS badge sitting beside the number — «this member is fine» — rather
    // than as an affordance, and the score is the one figure on the row a
    // manager is most likely to want the arithmetic behind.
    //
    // The negative margin keeps a finger-sized hit area without widening the
    // row: the icon occupies 15px, the button answers to 31.
    return (
        <button
            type="button"
            aria-label="جزئیات محاسبهٔ امتیاز"
            onClick={(event) => {
                // the row itself is a button; reading must not navigate
                event.stopPropagation()
                onClick()
            }}
            className=" -m-2 flex shrink-0 items-center justify-center p-2 text-text-muted"
        >
            <FiInfo aria-hidden size={15} />
        </button>
    )
}

export function MemberRow({
    row,
    sort,
    max,
    moneypoolId,
    dense,
    onExplainScore,
}: {
    row: IAdminMemberRow
    sort: TMembersSort
    max: number
    moneypoolId?: string
    dense?: boolean
    onExplainScore?: (row: IAdminMemberRow) => void
}) {
    const navigate = useNavigate()
    const spec = SORTS.find((option) => option.value === sort) ?? SORTS[0]
    const value = spec.read(row)
    const width =
        spec.scale === 'absolute'
            ? Math.min(Math.max(value, 0), 100)
            : max > 0
            ? Math.max((value / max) * 100, 0)
            : 0
    const chip = chipFor(row)

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() =>
                navigate(`/fund/member-record/${moneypoolId}/${row.poolship_id}`)
            }
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    navigate(
                        `/fund/member-record/${moneypoolId}/${row.poolship_id}`
                    )
                }
            }}
            className={` flex min-h-[44px] cursor-pointer flex-col justify-center gap-1.5 border-b border-divider py-3 last:border-b-0 ${
                dense ? '' : ' px-1'
            }`}
        >
            <div className=" flex items-center gap-2.5">
                <span className=" flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-soft text-[11px] font-bold text-brand-deep">
                    {(row.member_name || '؟').trim().charAt(0)}
                </span>
                {/* `overflow-hidden`, not just `min-w-0`: min-w-0 lets this
                    shrink past its contents but does NOT clip them, so the
                    chip — which cannot shrink, «۱۳۴ ر…» being useless — used
                    to paint straight over the value beside it. Clipping a chip
                    at an absurd width is a far smaller lie than two numbers
                    printed on top of each other. */}
                <div className=" flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
                    <p className=" truncate text-xs font-bold text-text-body">
                        {row.member_name}
                    </p>
                    {chip && (
                        <span
                            className={` shrink-0 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[9px] font-bold ${chip.className}`}
                        >
                            {chip.text}
                        </span>
                    )}
                </div>
                {sort === 'score' && onExplainScore && (
                    <ScoreInfoDot onClick={() => onExplainScore(row)} />
                )}
                <div className=" shrink-0 text-left">
                    <p className=" whitespace-nowrap text-xs font-bold tabular-nums text-text-body">
                        {spec.text(row)}
                    </p>
                    <p className=" mt-0.5 text-[10px] text-text-secondary">
                        {spec.unit}
                    </p>
                </div>
                <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#A7A7A7"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className=" shrink-0"
                >
                    <path d="M15 18l-6-6 6-6" />
                </svg>
            </div>

            {/* The track lives OUTSIDE the row above, and that is the whole
                point: its width must not depend on anything in the row.
                While it shared a line with the chip, rows with a chip got a
                shorter track — and since the fill is a percentage OF the
                track, a member with ۲۱٫۶ million drew a longer bar than the
                member above them with ۲۳٫۷. Putting it back beside the value
                would not have been enough either: `tabular-nums` does nothing
                for Persian digits in this font, so «۵۱٬۰۰۰٬۰۰۰» and
                «۳۵٬۵۰۰٬۰۰۰» differ by 6px and the track inherited that too.

                The fixed right margin clears the avatar (w-8 + gap-2.5) so the
                bar still starts under the name — a constant, so every track is
                the same width. Bars fill RIGHT-to-left, matching ScoreMeter and
                every other bar in the app; MemberRankCard's dir="ltr" is the
                outlier. */}
            <div className=" relative mr-[42px] h-[5px] overflow-hidden rounded-full bg-canvas">
                <span
                    className=" absolute inset-y-0 right-0 rounded-full"
                    style={{ width: `${width}%`, background: spec.color }}
                />
            </div>
        </div>
    )
}

export function SortPills({
    sort,
    onChange,
}: {
    sort: TMembersSort
    onChange: (value: TMembersSort) => void
}) {
    return (
        // wrapping, not one line: five pills — one of them «بدهی سررسیدشده» —
        // do not fit a phone, and without this they simply drew outside the
        // card and off the page
        <div className=" flex flex-wrap gap-1.5">
            {SORTS.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    onClick={() => option.value !== sort && onChange(option.value)}
                    className={` min-h-[36px] rounded-full px-3 text-[11px] ${
                        option.value === sort
                            ? ' bg-brand font-bold text-white'
                            : ' bg-canvas text-text-secondary'
                    }`}
                >
                    {option.label}
                </button>
            ))}
        </div>
    )
}
